/* ==========================================================================
   AYRAA — cart drawer additions

   Four behaviours the theme did not already have:

     1. <ay-cart-recs>     related products, fetched on first reveal
     2. <ay-cart-panels>   keeps the note / discount panel open across a
                           re-render, and keeps an unsaved note draft
     3. <ay-ship-protect>  a toggle that adds or removes a real line item
     4. quick add          from a recommendation card, with a variant select
                           for anything that has a size or colour to pick

   Everything else — quantity, remove, subtotal, discounts, checkout — is the
   theme's own code (ECXCartDrawerForm, ECXCartDiscount) and is left alone.

   These are custom elements rather than delegated listeners because the theme
   re-renders the whole of .js-cart-drawer-wrapper on every cart change
   (ECXCartDrawerForm.getSectionsToRender), so a listener bound to the old
   markup would be thrown away. Custom elements upgrade themselves on each
   render instead.
   ========================================================================== */
(function () {
  "use strict";

  /* The same two sections the theme's own cart notification asks for, mapped
     the same way, so a quick add lands in exactly the places a normal add
     to cart would. Requesting them on the POST keeps this to one round trip. */
  var SECTIONS = [
    { id: "cart-drawer", from: ".js-cart-drawer-wrapper", into: ".js-cart-drawer-wrapper" },
    { id: "header-cart-status", from: ".shopify-section", into: "#headerCartStatus" }
  ];
  var SECTION_IDS = SECTIONS.map(function (s) { return s.id; });

  /* Recommendations are re-fetched otherwise: the element is destroyed and
     rebuilt on every quantity change, and the response for a given source
     product does not change in between. Keyed by URL, so adding something new
     (which changes the source product) still fetches. */
  var recsCache = {};

  /* Which disclosure was open, and any note text not yet committed to the
     cart, carried across the re-render that would otherwise discard both. */
  var panelState = { note: false, discount: false };
  var noteDraft = null;

  function routes() {
    return window.routes || {};
  }

  function post(url, body) {
    return fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify(body)
    }).then(function (response) {
      return response.json();
    });
  }

  /* A refused or failed add must say so on the button -- re-enabling it
     silently reads as a button that does nothing. */
  function flashError(btn, message) {
    var html = btn.innerHTML;
    btn.textContent = message || "Couldn't add to bag";
    setTimeout(function () { btn.innerHTML = html; }, 2500);
  }

  function cartPayload(extra) {
    var body = { sections: SECTION_IDS, sections_url: window.location.pathname };
    for (var key in extra) body[key] = extra[key];
    return body;
  }

  /* Paint a cart response into the page. Falls back to a reload rather than
     leaving the drawer showing something that is no longer true. */
  function render(response) {
    if (!response || !response.sections) {
      window.location.reload();
      return false;
    }
    var painted = false;
    SECTIONS.forEach(function (map) {
      var html = response.sections[map.id];
      var target = document.querySelector(map.into);
      if (!html || !target) return;
      var fresh = new DOMParser().parseFromString(html, "text/html").querySelector(map.from);
      if (!fresh) return;
      target.innerHTML = fresh.innerHTML;
      painted = true;
    });
    if (!painted) window.location.reload();
    return painted;
  }

  /* ------------------------------------------------------------------ *
   * Related products
   *
   * Its own element rather than the theme's <ecx-prd-recom>, because that one
   * is defined in component-add-to-cart.js which only loads on the product
   * page — the drawer opens everywhere. Fetches on first reveal, so a closed
   * drawer costs nothing.
   * ------------------------------------------------------------------ */
  if (!window.customElements.get("ay-cart-recs")) {
    window.customElements.define(
      "ay-cart-recs",
      class AyCartRecs extends HTMLElement {
        connectedCallback() {
          var url = this.dataset.url;
          if (!url) return;

          if (recsCache[url]) {
            this.paint(recsCache[url]);
            return;
          }
          var self = this;
          this.observer = new IntersectionObserver(function (entries) {
            if (!entries[0].isIntersecting) return;
            self.observer.disconnect();
            self.load(url);
          });
          this.observer.observe(this);
        }

        disconnectedCallback() {
          if (this.observer) this.observer.disconnect();
        }

        load(url) {
          var self = this;
          fetch(url)
            .then(function (response) {
              return response.text();
            })
            .then(function (text) {
              var inner = new DOMParser().parseFromString(text, "text/html").querySelector(".ay-recs__inner");
              // nothing to recommend renders nothing at all, no empty heading
              recsCache[url] = inner ? inner.outerHTML : "";
              self.paint(recsCache[url]);
            })
            .catch(function (error) {
              console.error("[ayraa] cart recommendations failed", error);
            });
        }

        paint(html) {
          if (!html) return;
          this.innerHTML = html;
          this.bindCarousel();
          this.bindAdds();
        }

        bindCarousel() {
          var track = this.querySelector("[data-ay-recs-track]");
          if (!track) return;

          var step = function () {
            var item = track.querySelector(".ay-recs__item");
            if (!item) return 200;
            var styles = window.getComputedStyle(track);
            return item.getBoundingClientRect().width + (parseFloat(styles.columnGap) || 0);
          };

          var prev = this.querySelector("[data-ay-recs-prev]");
          var next = this.querySelector("[data-ay-recs-next]");
          if (prev) prev.addEventListener("click", function () { track.scrollBy({ left: -step(), behavior: "smooth" }); });
          if (next) next.addEventListener("click", function () { track.scrollBy({ left: step(), behavior: "smooth" }); });

          var syncArrows = function () {
            var max = track.scrollWidth - track.clientWidth - 1;
            if (prev) prev.disabled = track.scrollLeft <= 0;
            if (next) next.disabled = track.scrollLeft >= max;
          };
          track.addEventListener("scroll", syncArrows, { passive: true });
          syncArrows();
        }

        bindAdds() {
          var self = this;

          /* one sellable variant: straight into the cart */
          this.querySelectorAll("[data-ay-cart-add]").forEach(function (btn) {
            btn.addEventListener("click", function () {
              self.add(btn, btn.dataset.ayCartAdd);
            });
          });

          /* more than one: reveal the select rather than send them away */
          this.querySelectorAll("[data-ay-recs-choose]").forEach(function (btn) {
            btn.addEventListener("click", function () {
              var pick = btn.parentElement.querySelector("[data-ay-recs-pick]");
              if (!pick) return;
              btn.hidden = true;
              btn.setAttribute("aria-expanded", "true");
              pick.hidden = false;
              var select = pick.querySelector("[data-ay-recs-variant]");
              if (select) select.focus();
            });
          });

          this.querySelectorAll("[data-ay-cart-add-selected]").forEach(function (btn) {
            btn.addEventListener("click", function () {
              var select = btn.parentElement.querySelector("[data-ay-recs-variant]");
              if (select && select.value) self.add(btn, select.value);
            });
          });
        }

        add(btn, variantId) {
          if (!variantId || btn.disabled) return;
          btn.disabled = true;
          btn.classList.add("is-busy");
          post(routes().cart_add_url || "/cart/add", cartPayload({ items: [{ id: variantId, quantity: 1 }] }))
            .then(function (response) {
              // a refusal comes back with a status and no sections; leave the
              // drawer as it is and put the button back
              if (response && response.status && !response.sections) {
                flashError(btn, response.description || response.message);
                return;
              }
              render(response);
            })
            .catch(function (error) {
              console.error("[ayraa] quick add failed", error);
              flashError(btn);
            })
            .finally(function () {
              btn.disabled = false;
              btn.classList.remove("is-busy");
            });
        }
      }
    );
  }

  /* ------------------------------------------------------------------ *
   * Order note and discount disclosures
   *
   * Both collapse by default. Without this they would also collapse every
   * time a quantity changed, because the wrapper they live in is replaced.
   * ------------------------------------------------------------------ */
  if (!window.customElements.get("ay-cart-panels")) {
    window.customElements.define(
      "ay-cart-panels",
      class AyCartPanels extends HTMLElement {
        connectedCallback() {
          var found = [];
          this.querySelectorAll("[data-ay-panel]").forEach(function (cell) {
            var name = cell.dataset.ayPanel;
            // the note cell is itself a <details>; the discount cell wraps one
            var details = cell.tagName === "DETAILS" ? cell : cell.querySelector("details");
            if (!details) return;
            details.open = !!panelState[name];
            found.push({ name: name, details: details });
          });

          /* One at a time. Two open panels push the totals and the checkout
             button most of the way off a phone, which is the thing this
             redesign is meant to stop. */
          found.forEach(function (panel) {
            panel.details.addEventListener("toggle", function () {
              panelState[panel.name] = panel.details.open;
              if (!panel.details.open) return;
              found.forEach(function (other) {
                if (other === panel || !other.details.open) return;
                other.details.open = false;
                panelState[other.name] = false;
              });
            });
          });

          var note = this.querySelector("#cartDrawerNoteInput");
          if (!note) return;
          if (noteDraft !== null && noteDraft !== note.value) note.value = noteDraft;
          note.addEventListener("input", function () {
            noteDraft = note.value;
          });
        }
      }
    );
  }

  /* ------------------------------------------------------------------ *
   * Shipping protection
   * ------------------------------------------------------------------ */
  if (!window.customElements.get("ay-ship-protect")) {
    window.customElements.define(
      "ay-ship-protect",
      class AyShipProtect extends HTMLElement {
        connectedCallback() {
          this.input = this.querySelector("input[type=checkbox]");
          if (!this.input || this.dataset.variantId === "") return;
          this.variantId = this.dataset.variantId;
          this.lineKey = this.dataset.lineKey || "";
          this.input.addEventListener("change", this.onToggle.bind(this));
        }

        setBusy(busy) {
          this.classList.toggle("is-busy", busy);
          if (this.input) this.input.disabled = busy;
        }

        onToggle() {
          var self = this;
          var wanted = this.input.checked;
          this.setBusy(true);

          var request = wanted
            ? post(routes().cart_add_url || "/cart/add", cartPayload({ items: [{ id: this.variantId, quantity: 1 }] }))
            : post(routes().cart_change_url || "/cart/change", cartPayload({ id: this.lineKey || this.variantId, quantity: 0 }));

          request
            .then(function (response) {
              if (response && response.status && !response.sections) {
                // the cart refused the change; put the control back
                self.input.checked = !wanted;
                return;
              }
              render(response);
            })
            .catch(function (error) {
              console.error("[ayraa] shipping protection failed", error);
              self.input.checked = !wanted;
            })
            .finally(function () {
              self.setBusy(false);
            });
        }
      }
    );
  }
})();
